import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api, type MedicineName, type SubstituteOption } from "../api";
import { taka } from "../format";
import { C, eyebrow, font, pill } from "./look";

// P3 · Suggest a substitute: brands with the same generic, strength and form,
// on-shelf first with this pharmacy's strip price; the price difference; and
// the message the customer will see.

export type Chosen = { medicine: SubstituteOption; unitPricePaisa: number };

export function SubstituteDialog({
  orderId,
  pharmacyName,
  medicineId,
  medicine,
  strips,
  unitPricePaisa,
  initial,
  onCancel,
  onSend,
}: {
  orderId: string;
  pharmacyName: string;
  medicineId: number;
  medicine: MedicineName;
  strips: number;
  unitPricePaisa: number;
  initial: Chosen | null;
  onCancel: () => void;
  onSend: (c: Chosen) => void;
}) {
  const options = useQuery({
    queryKey: ["substitutes", medicineId],
    queryFn: () => api<{ medicine: (MedicineName & { genericName: string }) | null; substitutes: SubstituteOption[] }>(`/staff/substitutes?medicineId=${medicineId}`),
  });
  const [picked, setPicked] = useState<SubstituteOption | null>(initial?.medicine ?? null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const name = medicine ? [medicine.brandName, medicine.strength].filter(Boolean).join(" ") : `Medicine #${medicineId}`;
  const original = options.data?.medicine;
  const generic = original ? `${original.genericName} ${original.strength}`.trim() : "";
  const form = original?.dosageForm.toLowerCase() ?? "";
  const usable = (options.data?.substitutes ?? []).filter((s) => s.stripPricePaisa !== null);
  const diff = picked?.stripPricePaisa != null ? (picked.stripPricePaisa - unitPricePaisa) * strips : null;

  return (
    <div
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(14, 23, 22, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 16px", overflowY: "auto", fontFamily: font.sans, color: C.ink }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sub-title"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 640, background: C.surface, borderRadius: 28, padding: 24, boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 18, margin: "auto" }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <h1 id="sub-title" style={{ margin: 0, fontSize: 22, lineHeight: "28px", fontWeight: 700, letterSpacing: "-0.01em" }}>
              Suggest a substitute
            </h1>
            <div style={{ fontSize: 13, lineHeight: "18px", color: C.muted }}>
              Order {orderId} · {name} · {strips} strip{strips === 1 ? "" : "s"}
            </div>
          </div>
          <button aria-label="Close" onClick={onCancel} style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 999, border: `1px solid ${C.line}`, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div style={{ background: C.tint, borderRadius: 14, padding: "12px 14px", fontSize: 13, lineHeight: "18px", color: C.body }}>
          Only brands with the <span style={{ fontWeight: 600, color: C.ink }}>same generic, strength and form</span> are listed
          {generic ? `: ${generic}, ${form}.` : "."}
        </div>

        <div role="radiogroup" aria-label="Substitute brand" style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 340, overflowY: "auto" }}>
          {options.isLoading && <div style={{ color: C.muted }}>Finding brands…</div>}
          {!options.isLoading && usable.length === 0 && (
            <div style={{ color: C.muted, fontSize: 15 }}>No other brand of this medicine is in your price list. Mark it unavailable instead.</div>
          )}
          {usable.map((s) => {
            const on = picked?.medicineId === s.medicineId;
            return (
              <button
                key={s.medicineId}
                role="radio"
                aria-checked={on}
                onClick={() => setPicked(s)}
                style={{
                  width: "100%",
                  minHeight: 64,
                  boxSizing: "border-box",
                  border: on ? `2px solid ${C.teal}` : `1px solid ${C.line}`,
                  borderRadius: 16,
                  background: C.surface,
                  padding: on ? "12px 15px" : "13px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  textAlign: "left",
                  color: C.ink,
                  cursor: "pointer",
                  opacity: s.inStock ? 1 : 0.6,
                }}
              >
                <span aria-hidden="true" style={{ width: 22, height: 22, flexShrink: 0, boxSizing: "border-box", borderRadius: 999, border: on ? `7px solid ${C.teal}` : `2px solid ${C.muted}` }} />
                <span style={{ display: "flex", flexDirection: "column", flexGrow: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 16, lineHeight: "22px" }}>
                    <span style={{ fontWeight: 600 }}>{s.brandName}</span> {s.strength}
                  </span>
                  <span style={{ fontSize: 13, lineHeight: "18px", color: C.muted }}>
                    {s.manufacturer || s.dosageForm} · {s.inStock ? "on shelf" : "marked out of stock"}
                  </span>
                </span>
                <span style={{ fontSize: 15, fontWeight: 700, color: C.priceStrong, whiteSpace: "nowrap" }}>{taka(s.stripPricePaisa!)} / strip</span>
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 8, fontSize: 15, lineHeight: "22px", padding: "12px 0", borderTop: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}` }}>
          <span style={{ color: C.body }}>
            Price difference for {strips} strip{strips === 1 ? "" : "s"}
          </span>
          <span style={{ fontWeight: 700 }}>{diff === null ? "—" : `${diff >= 0 ? "+ " : "− "}${taka(Math.abs(diff))}`}</span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={eyebrow}>THE CUSTOMER WILL SEE</div>
          <div style={{ border: `1px solid ${C.line}`, borderRadius: 14, padding: "12px 14px", fontSize: 15, lineHeight: "22px", color: C.body }}>
            {medicine?.brandName ?? name} is out of stock. {pharmacyName} suggests {picked ? picked.brandName : "another brand"}, the same medicine
            {generic ? ` (${generic.toLowerCase()})` : ""} from another company. Approve it, or remove it from your order.
          </div>
        </div>

        <div style={{ fontSize: 13, lineHeight: "18px", color: C.muted }}>
          The order waits for the customer's answer. With no answer in 30 minutes, this medicine is removed and the rest of the order goes ahead.
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 12 }}>
          <button onClick={onCancel} style={pill("secondary", 52)}>
            Cancel
          </button>
          <button
            disabled={!picked || picked.stripPricePaisa === null}
            onClick={() => picked && picked.stripPricePaisa !== null && onSend({ medicine: picked, unitPricePaisa: picked.stripPricePaisa })}
            style={{ ...pill("primary", 52), opacity: picked ? 1 : 0.5 }}
          >
            Send to customer
          </button>
        </div>
      </div>
    </div>
  );
}
